/**
 * The screens, joined up.
 *
 * Everything this component needs arrives through `data` and `actions`, so the
 * whole flow — pick a project, open a board, open a breaker, photograph a shot —
 * runs in a test against fakes with no tenant, no camera and no IndexedDB. The
 * real wiring lives in `main.tsx` and is thin enough to read in one screen.
 *
 * Two things are held here rather than pushed down:
 *
 *  - **capture state**, because a breaker's answers must survive walking back to
 *    the lineup and returning; and
 *  - **the camera**, because one hidden input serves every slot on the page.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { Asset, CapturedShot, ShotProfile, TemplateItem } from '../domain/types.js';
import type { ResultInput } from '../domain/verdict.js';
import type { Loaded, Register, Script } from '../data/load.js';
import type { ProjectRef } from '../api/sp.js';
import type { Picked } from '../capture/camera.js';
import {
  emptyCapture,
  record,
  recordShot,
  summariseBreaker,
  viewScope,
  type CaptureState,
  type Scope,
} from '../domain/capture.js';
import { describeBand } from '../domain/verdict.js';
import {
  back as navBack,
  backLabel,
  chooseProject,
  chooseTarget,
  initialNav,
  openBoard,
  openBoardScript,
  openBreaker,
  type NavState,
} from '../app/nav.js';
import type { Platform } from '../platform/ios.js';
import { BoardList, boardProgress } from './BoardList.js';
import { TargetPicker } from './TargetPicker.js';
import { BreakerPage } from './BreakerPage.js';
import { CheckRow } from './CheckRow.js';
import { CameraInput, type CameraHandle } from './CameraInput.js';
import { DeviceStatus } from './DeviceStatus.js';
import { Freshness } from './Freshness.js';
import { LineupList } from './LineupList.js';

export interface ShellData {
  projects: Loaded<ProjectRef[]> | null;
  register: Loaded<Register> | null;
  script: Loaded<Script> | null;
  profiles: readonly ShotProfile[];
  project: ProjectRef | null;
  platform: Platform;
  online: boolean;
  pendingPhotos: number;
  bytesHeld: number;
}

export interface ShellActions {
  chooseProject: (p: ProjectRef) => void;
  /** Told what is being tested, so the loader can fetch the right script and shots. */
  chooseTarget?: ((equipmentType: string, level: string) => void) | undefined;
  leaveProject: () => void;
  /** Persists one answer. The screen has already rendered it. */
  saveAnswer: (scope: Scope, item: TemplateItem, input: ResultInput) => void;
  /** Persists one photo and returns what the slot should show. */
  savePhoto: (scope: Scope, profile: ShotProfile, picked: Picked) => Promise<CapturedShot>;
  syncNow?: (() => void) | undefined;
  retry?: (() => void) | undefined;
}

const refOf = (a: Asset): string => a.tag.split('/').pop() ?? a.tag;

export function AppShell({ data, actions }: { data: ShellData; actions: ShellActions }) {
  const [nav, setNav] = useState<NavState>(initialNav);
  const [capture, setCapture] = useState<CaptureState>(emptyCapture);
  const [problem, setProblem] = useState<string | null>(null);
  const [installDismissed, setInstallDismissed] = useState(false);
  const camera = useRef<CameraHandle>(null);
  // Which breaker the open picker belongs to. A ref because it is read in an
  // async callback and must not trigger a render between tap and camera.
  const shotScope = useRef<Scope | null>(null);

  const register = data.register?.data;
  const script = data.script?.data;

  const boards = register?.boards ?? [];
  const board = useMemo(() => {
    const route = nav.route;
    if (route.screen === 'projects' || route.screen === 'target' || route.screen === 'boards') return null;
    return boards.find((b) => b.id === route.boardId) ?? null;
  }, [boards, nav.route]);
  const breakers = board ? register?.breakersByBoard[board.tag] ?? [] : [];

  const summaryOf = useCallback(
    (b: Asset) => {
      if (!script || !board) return null;
      return summariseBreaker(
        capture,
        { assetId: board.id, breakerRef: refOf(b) },
        script.sections,
        script.items,
        data.profiles,
        b.breakerType,
      );
    },
    [capture, script, board, data.profiles],
  );

  const onChooseProject = useCallback(
    (p: ProjectRef) => {
      // Capture state is per project as strictly as the reads are: an answer
      // typed against MY02 must not survive into another project's screen.
      setCapture(emptyCapture());
      setNav((n) => chooseProject(n, p.id));
      actions.chooseProject(p);
    },
    [actions],
  );

  const onAnswer = useCallback(
    (scope: Scope, item: TemplateItem, input: ResultInput) => {
      setCapture((c) => record(c, scope, item.id, input));
      actions.saveAnswer(scope, item, input);
    },
    [actions],
  );

  const onPhoto = useCallback(
    async (profile: ShotProfile, picked: Picked) => {
      const scope = shotScope.current;
      if (!scope) return;
      try {
        const shot = await actions.savePhoto(scope, profile, picked);
        setCapture((c) => recordShot(c, scope, shot));
        setProblem(null);
      } catch (e) {
        // The slot stays empty. A green slot for a photo that was not stored
        // survives nothing, and the engineer would never take it again.
        setProblem(e instanceof Error ? e.message : String(e));
      }
    },
    [actions],
  );

  return (
    <main className="shell">
      <header className="shell-head">
        {backLabel(nav.route, { board: board?.tag }) ? (
          <button type="button" className="back" onClick={() => setNav(navBack)}>
            ‹ {backLabel(nav.route, { board: board?.tag })}
          </button>
        ) : null}
        <h1>Cx Field Capture</h1>
      </header>

      <DeviceStatus
        platform={data.platform}
        bytesHeld={data.bytesHeld}
        pendingPhotos={data.pendingPhotos}
        online={data.online}
        installDismissed={installDismissed}
        onDismissInstall={() => setInstallDismissed(true)}
        {...(actions.syncNow ? { onSyncNow: actions.syncNow } : {})}
      />

      {problem ? (
        <p className="warn" role="alert" data-testid="capture-problem">
          {problem}
        </p>
      ) : null}

      {nav.route.screen === 'projects' ? (
        <section aria-label="Projects">
          {data.projects ? <Freshness loaded={data.projects} {...(actions.retry ? { onRetry: actions.retry } : {})} /> : null}
          <h2>Choose a project</h2>
          <ul>
            {(data.projects?.data ?? []).map((p) => (
              <li key={p.id}>
                <button type="button" className="row" onClick={() => onChooseProject(p)}>
                  <span className="t">{p.name}</span>
                  <span className="d">{p.code}</span>
                </button>
              </li>
            ))}
          </ul>
          {/*
            "No projects yet" is a statement about the tenant. It is only true
            when a read came back empty — not when no read has landed. The two
            looked identical on screen and the wrong one was being shown.
          */}
          {data.projects && data.projects.data.length === 0 ? (
            <p className="empty" data-testid="no-projects">
              No projects yet.
            </p>
          ) : null}
          {!data.projects ? (
            <p className="empty" data-testid="projects-unloaded">
              Projects have not loaded.
            </p>
          ) : null}
        </section>
      ) : null}

      {nav.route.screen === 'target' ? (
        <TargetPicker
          projectName={data.project?.name ?? 'This project'}
          onChoose={(equipmentType, level) => {
            setNav((n) => chooseTarget(n, equipmentType, level));
            actions.chooseTarget?.(equipmentType, level);
          }}
        />
      ) : null}

      {nav.route.screen === 'boards' ? (
        <>
          {data.register ? <Freshness loaded={data.register} {...(actions.retry ? { onRetry: actions.retry } : {})} /> : null}
          <BoardList
            projectName={data.project?.name ?? ''}
            boards={boards}
            progress={Object.fromEntries(
              boards.map((b) => [
                b.tag,
                boardProgress(
                  (register?.breakersByBoard[b.tag] ?? [])
                    .map((brk) =>
                      script
                        ? summariseBreaker(
                            capture,
                            { assetId: b.id, breakerRef: refOf(brk) },
                            script.sections,
                            script.items,
                            data.profiles,
                            brk.breakerType,
                          )
                        : null,
                    )
                    .filter((s): s is NonNullable<typeof s> => s !== null),
                ),
              ]),
            )}
            onOpen={(b) => setNav((n) => openBoard(n, b.id))}
          />
          <button type="button" onClick={() => { setNav(initialNav()); actions.leaveProject(); }}>
            Choose another project
          </button>
        </>
      ) : null}

      {nav.route.screen === 'lineup' && board ? (
        <LineupList
          board={board}
          breakers={breakers}
          summaries={Object.fromEntries(
            breakers.map((b) => [refOf(b), summaryOf(b)]).filter(([, s]) => s) as [string, NonNullable<ReturnType<typeof summaryOf>>][],
          )}
          onOpen={(b) => setNav((n) => openBreaker(n, board.id, refOf(b)))}
          onOpenBoardScript={() => setNav((n) => openBoardScript(n, board.id))}
        />
      ) : null}

      {nav.route.screen === 'boardScript' && board && script ? (
        (() => {
          // No `breakerRef`: the scope is the board itself, so `viewScope`
          // returns the board-level sections and none of the per-breaker ones.
          const scope: Scope = { assetId: board.id };
          const view = viewScope(capture, scope, script.sections, script.items, { describeBand });
          return (
            <section className="board-script" aria-label={`${board.tag} board script`}>
              <header>
                <h2>{board.tag}</h2>
                <p>Checks that belong to the board</p>
                <span className={`chip ${view.overall.overall.toLowerCase().replace('/', '')}`} data-testid="board-overall">
                  {view.overall.overall}
                </span>
              </header>
              {view.sections.map((section) => (
                <section className="script-section" key={section.section.id} aria-label={section.section.name}>
                  <h3>{section.section.name}</h3>
                  {section.checks.map((check) => (
                    <CheckRow
                      key={check.item.id}
                      check={check}
                      onChange={(input) => onAnswer(scope, check.item, input)}
                    />
                  ))}
                </section>
              ))}
              {view.sections.length === 0 ? (
                <p className="empty" data-testid="no-board-checks">
                  This script has no board-level checks.
                </p>
              ) : null}
            </section>
          );
        })()
      ) : null}

      {nav.route.screen === 'breaker' && board && script ? (
        (() => {
          const ref = nav.route.breakerRef;
          const breaker = breakers.find((b) => refOf(b) === ref);
          if (!breaker) {
            // A register that changed under the engineer. Falling back beats a
            // blank screen with no way out.
            return (
              <p className="empty" data-testid="breaker-gone">
                {ref} is no longer on this board.
              </p>
            );
          }
          const scope: Scope = { assetId: board.id, breakerRef: ref };
          const view = viewScope(capture, scope, script.sections, script.items, {
            profiles: data.profiles,
            breakerType: breaker.breakerType,
            describeBand,
          });
          return (
            <BreakerPage
              breaker={breaker}
              board={board}
              view={view}
              profiles={data.profiles.filter((p) => p.breakerType === breaker.breakerType)}
              captured={capture.shots[`${scope.assetId}/${scope.breakerRef}`] ?? {}}
              onAnswer={(item, input) => onAnswer(scope, item, input)}
              onCapture={(profile) => {
                // Synchronous on purpose: Safari only opens the camera inside
                // the tap that asked for it.
                shotScope.current = scope;
                camera.current?.open(profile);
              }}
            />
          );
        })()
      ) : null}

      <CameraInput
        ref={camera}
        onPhoto={onPhoto}
        onProblem={(_, message) => setProblem(message)}
      />
    </main>
  );
}

